import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Controller } from 'react-hook-form';
import { Circle } from 'lucide-react';
import * as React from 'react';

import { AritoFormV2Context } from '@/components/custom/erp/form-v2/context';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return <RadioGroupPrimitive.Root className={cn('grid gap-2', className)} {...props} ref={ref} />;
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        'aspect-square h-4 w-4 rounded-full border border-primary text-primary ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-blue-500 data-[state=checked]:bg-blue-500',
        className
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className='flex items-center justify-center'>
        <Circle className='h-2.5 w-2.5 fill-white text-white' />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

interface RadioButtonProps {
  options: { value: string; label: string }[];
  name: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  className?: string;
  orientation?: 'horizontal' | 'vertical';
  disabled?: boolean;
}

const RadioButton: React.FC<RadioButtonProps> = ({
  options,
  name,
  defaultValue,
  onChange,
  className,
  orientation = 'horizontal',
  disabled
}) => {
  const v2Context = React.useContext(AritoFormV2Context);
  const formContext = v2Context;
  const control = formContext?.control;
  const isViewMode = formContext?.isViewMode ?? false;
  const isDisabled = disabled ?? isViewMode;
  const orientationClass = orientation === 'horizontal' ? 'flex flex-row gap-6' : 'flex flex-col gap-2';

  const renderOptions = () =>
    options.map(option => (
      <div key={option.value} className='flex items-center space-x-2'>
        <RadioGroupItem value={option.value} id={`${name}-${option.value}`} disabled={isDisabled} />
        <Label htmlFor={`${name}-${option.value}`} className='cursor-pointer'>
          {option.label}
        </Label>
      </div>
    ));

  if (control && name) {
    const fallbackValue = defaultValue ?? options[0]?.value ?? '';

    return (
      <Controller
        name={name}
        control={control}
        defaultValue={fallbackValue}
        render={({ field }) => (
          <RadioGroup
            name={name}
            value={field.value ?? fallbackValue}
            onValueChange={value => {
              field.onChange(value);
              onChange?.(value);
            }}
            className={cn(orientationClass, className)}
          >
            {renderOptions()}
          </RadioGroup>
        )}
      />
    );
  }

  return (
    <RadioGroup
      defaultValue={defaultValue}
      name={name}
      onValueChange={value => {
        onChange?.(value);
      }}
      className={cn(orientationClass, className)}
    >
      {renderOptions()}
    </RadioGroup>
  );
};

export { RadioGroup, RadioGroupItem, RadioButton };
